import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AñoEscolar } from 'types/usuario.types';
import { EstadoCuenta, PagoMatriculaResumen, PensionItem } from 'types/pago.types';
import { estadoCuentaService } from 'services/pagoService';
import { catalogoService } from 'services/usuarioService';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// MisPagosPage – Estado de cuenta del estudiante (solo lectura)
// ============================================================

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const ESTADO_STYLE: Record<string, { badge: string; dot: string }> = {
  PENDIENTE: { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  PAGADO:    { badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  ANULADO:   { badge: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400' },
};

const sol = (n: number | null) =>
  n == null ? '—' :
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const SpinnerIcon = () => (
  <svg className="animate-spin w-6 h-6 text-blue-500" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Tarjeta resumen ───────────────────────────────────────────
const ResumenCard = ({ label, value, sub, color }: {
  label: string; value: string; sub?: string; color: string;
}) => (
  <div className={`rounded-2xl px-5 py-4 ${color}`}>
    <p className="text-xs font-semibold opacity-70">{label}</p>
    <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
    {sub && <p className="text-xs opacity-60 mt-0.5 tabular-nums">{sub}</p>}
  </div>
);

// ── Card pago matrícula ───────────────────────────────────────
const CardMatricula: React.FC<{ pago: PagoMatriculaResumen }> = ({ pago }) => {
  const est = ESTADO_STYLE[pago.estado] ?? ESTADO_STYLE.PENDIENTE;
  const pagado = pago.estado === 'PAGADO';
  return (
    <div className={`rounded-2xl border p-5 space-y-4
      ${pagado ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center
            ${pagado ? 'bg-green-100' : 'bg-amber-100'}`}>
            <svg viewBox="0 0 20 20" fill="currentColor"
              className={`w-5 h-5 ${pagado ? 'text-green-600' : 'text-amber-600'}`}>
              <path fillRule="evenodd" d="M1 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4Zm12 4a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM4 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm13-1a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" clipRule="evenodd"/>
            </svg>
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">Pago de Matrícula</p>
            {pago.descuentoNombre && (
              <p className="text-xs text-indigo-600 mt-0.5">Descuento: {pago.descuentoNombre}</p>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${est.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${est.dot}`} />
          {pago.estado}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Tarifa',    value: sol(pago.montoTarifa) },
          { label: 'Descuento', value: sol(pago.montoDescuento) },
          { label: 'Total',     value: sol(pago.montoFinal) },
          { label: pagado ? 'Pagado el' : 'Monto pagado',
            value: pagado ? (pago.fechaPago ?? '—') : sol(pago.montoPagado) },
        ].map(c => (
          <div key={c.label} className="bg-white/70 rounded-xl px-3 py-2.5">
            <p className="text-xs text-slate-400">{c.label}</p>
            <p className="font-semibold text-slate-800 text-sm tabular-nums mt-0.5">{c.value}</p>
          </div>
        ))}
      </div>

      {!pagado && (
        <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
          Tu pago de matrícula está pendiente. Acércate a secretaría para regularizarlo.
        </p>
      )}
    </div>
  );
};

// ── Lista pensiones ───────────────────────────────────────────
const FilaPension: React.FC<{ p: PensionItem }> = ({ p }) => {
  const est    = ESTADO_STYLE[p.estado] ?? ESTADO_STYLE.PENDIENTE;
  const pagado  = p.estado === 'PAGADO';
  const anulado = p.estado === 'ANULADO';

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
      ${pagado  ? 'border-green-200 bg-green-50/30'
      : anulado ? 'border-slate-100 bg-slate-50/50 opacity-60'
      :           'border-slate-200 bg-white hover:border-amber-200'}`}>

      {/* Mes */}
      <div className={`w-14 flex-shrink-0 rounded-xl py-2 flex flex-col items-center
        text-xs font-bold
        ${pagado ? 'bg-green-100 text-green-700'
        : anulado ? 'bg-slate-100 text-slate-400'
        :           'bg-blue-50 text-blue-700'}`}>
        <span className="text-sm leading-tight capitalize">{p.mesNombre.slice(0, 3)}</span>
        <span className="opacity-60 text-[10px]">{p.anioPension}</span>
      </div>

      {/* N° cuota */}
      <span className="text-xs text-slate-400 w-8 flex-shrink-0 text-center">#{p.numeroPension}</span>

      {/* Montos */}
      <div className="flex-1 grid grid-cols-2 gap-x-4 text-xs min-w-0">
        <div>
          <p className="text-slate-400">Total</p>
          <p className="font-bold text-slate-800 tabular-nums">{sol(p.montoFinal)}</p>
        </div>
        <div>
          {pagado ? (
            <>
              <p className="text-slate-400">Pagado</p>
              <p className="font-semibold text-green-700 tabular-nums">{sol(p.montoPagado)}</p>
            </>
          ) : p.fechaVencimiento ? (
            <>
              <p className="text-slate-400">Vence</p>
              <p className="font-semibold text-slate-600">{p.fechaVencimiento}</p>
            </>
          ) : null}
        </div>
      </div>

      {/* Estado */}
      <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-bold
                        px-2.5 py-1 rounded-full ${est.badge}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${est.dot}`} />
        {pagado ? 'Pagado' : anulado ? 'Anulado' : 'Pendiente'}
      </span>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const MisPagosPage: React.FC = () => {
  const { user } = useAuth();

  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [anioId, setAnioId]               = useState<number | ''>('');

  const [cuenta, setCuenta]               = useState<EstadoCuenta | null>(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);

  // Carga catálogos
  useEffect(() => {
    catalogoService.getAñosEscolares().then(data => {
      const años = Array.isArray(data) ? data : [];
      setAñosEscolares(años);
      if (años.length > 0) setAnioId(años[0].idAnioEscolar);
    }).catch(() => {});
  }, []);

  // Carga estado de cuenta
  const cargar = useCallback(async (año: number | '') => {
    if (!año || !user) return;
    setLoading(true); setError(null); setCuenta(null);
    try {
      const data = await estadoCuentaService.getByEstudiante(user.idUsuario, año as number);
      setCuenta(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar tu estado de cuenta');
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (anioId) cargar(anioId); }, [anioId, cargar]);

  // Cuotas pendientes
  const pendientes = cuenta?.pensiones.filter(p => p.estado === 'PENDIENTE') ?? [];

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Mis Pagos</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Consulta el estado de tu matrícula y pensiones
          </p>
        </div>
        <select value={anioId}
          onChange={e => setAnioId(e.target.value ? Number(e.target.value) : '')}
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm
                     text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]
                     self-start sm:self-auto">
          {añosEscolares.map(a => (
            <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex justify-center py-20"><SpinnerIcon /></div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 text-red-600 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && cuenta && (
        <div className="space-y-5">

          {/* Banner del estudiante */}
          <div className="bg-gradient-to-r from-blue-700 to-blue-500 rounded-2xl px-6 py-5
                          flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-md">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center
                              text-white font-bold text-lg flex-shrink-0">
                {cuenta.estudianteNombres.charAt(0)}{cuenta.estudianteApellidos.charAt(0)}
              </div>
              <div>
                <p className="text-white font-bold text-base">
                  {cuenta.estudianteNombres} {cuenta.estudianteApellidos}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                    ${NIVEL_COLOR[cuenta.nivel] ?? 'bg-white/20 text-white'}`}>
                    {cuenta.nivel}
                  </span>
                  <span className="text-blue-100 text-xs">
                    {cuenta.gradoNombre} — Sección {cuenta.seccionNombre}
                  </span>
                  <span className="text-blue-200 text-xs">· {cuenta.anio}</span>
                </div>
              </div>
            </div>
            {pendientes.length > 0 && (
              <div className="bg-white/20 rounded-xl px-4 py-2.5 text-center flex-shrink-0">
                <p className="text-white/80 text-xs">Cuotas pendientes</p>
                <p className="text-white font-bold text-2xl">{pendientes.length}</p>
              </div>
            )}
          </div>

          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ResumenCard
              label="Total cargado"
              value={sol(cuenta.resumen.totalCargado)}
              color="bg-slate-100 text-slate-700"
            />
            <ResumenCard
              label="Total pagado"
              value={sol(cuenta.resumen.totalPagado)}
              color="bg-green-100 text-green-700"
            />
            <ResumenCard
              label="Por pagar"
              value={sol(cuenta.resumen.totalPendiente)}
              color="bg-amber-100 text-amber-700"
            />
            <ResumenCard
              label="Cuotas pagadas"
              value={`${cuenta.resumen.cuotasPagadas} / ${cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes}`}
              sub={`${cuenta.resumen.cuotasAnuladas > 0 ? `${cuenta.resumen.cuotasAnuladas} anulada(s)` : ''}`}
              color="bg-blue-100 text-blue-700"
            />
          </div>

          {/* Barra de progreso global */}
          {(cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes) > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Progreso de pago
                </p>
                <p className="text-xs font-bold text-slate-600">
                  {Math.round((cuenta.resumen.cuotasPagadas /
                    (cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes)) * 100)}%
                </p>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all"
                  style={{
                    width: `${(cuenta.resumen.cuotasPagadas /
                      (cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes)) * 100}%`
                  }} />
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-400">
                <span>{cuenta.resumen.cuotasPagadas} pagada{cuenta.resumen.cuotasPagadas !== 1 ? 's' : ''}</span>
                <span>{cuenta.resumen.cuotasPendientes} pendiente{cuenta.resumen.cuotasPendientes !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}

          {/* Layout dos columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Columna izquierda: matrícula */}
            <div className="space-y-4">
              {cuenta.pagoMatricula ? (
                <CardMatricula pago={cuenta.pagoMatricula} />
              ) : (
                <div className="rounded-2xl border border-slate-200 p-5 text-center text-slate-400 text-sm">
                  Sin pago de matrícula registrado
                </div>
              )}

              {/* Aviso si hay pendientes */}
              {pendientes.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 space-y-1">
                  <p className="text-sm font-bold text-amber-700">
                    Tienes {pendientes.length} cuota{pendientes.length !== 1 ? 's' : ''} pendiente{pendientes.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-amber-600">
                    Por pagar: <strong className="tabular-nums">{sol(cuenta.resumen.totalPendiente)}</strong>
                  </p>
                  <p className="text-xs text-amber-500 mt-1">
                    Acércate a secretaría para realizar tus pagos.
                  </p>
                </div>
              )}
            </div>

            {/* Columna derecha: pensiones */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-slate-700 text-sm">Pensiones mensuales</h2>
                <span className="text-xs text-slate-400">{cuenta.pensiones.length} cuotas</span>
              </div>
              <div className="p-4 space-y-2">
                {cuenta.pensiones.length === 0 ? (
                  añosEscolares.find(a => a.idAnioEscolar === anioId)?.fase === 'RECUPERACION' ? (
                    <div className="text-center py-8 px-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-3">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                          <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <p className="text-sm font-semibold text-slate-700 m-0">Año de Recuperación</p>
                      <p className="text-xs text-slate-500 mt-1">
                        En este período solo se cobra la matrícula. No hay pensiones mensuales.
                      </p>
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 text-sm py-8">
                      No hay pensiones registradas
                    </p>
                  )
                ) : (
                  cuenta.pensiones.map(p => <FilaPension key={p.idPension} p={p} />)
                )}
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Estado vacío */}
      {!loading && !error && !cuenta && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-7 h-7 text-blue-400">
              <path fillRule="evenodd" d="M1 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4Zm12 4a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM4 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm13-1a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" clipRule="evenodd"/>
            </svg>
          </div>
          <p className="text-slate-500 font-semibold">Sin información de pagos</p>
          <p className="text-slate-300 text-sm mt-1">Selecciona un año escolar para consultar</p>
        </div>
      )}
    </div>
  );
};

export default MisPagosPage;
